/**
 * playSlice.ts
 *
 * Lightweight Zustand slice for in-game status tracking.
 * Stores per-figure status (health, armor/will overrides, tags, notes)
 * and an optional informational phase indicator.
 *
 * Does NOT enforce rules, derive penalties, or mutate base warband stats.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Phase = 'Wizard' | 'Apprentice' | 'Soldier' | 'Creature';

export type EntityRef =
  | { kind: 'wizard' }
  | { kind: 'apprentice' }
  | { kind: 'soldier'; id: string }
  | { kind: 'custom'; id?: string };

export interface StatusHealth {
  current?: number | undefined;
  max?: number | undefined;
}

export interface Status {
  /** Reference back to the logical entity this status belongs to */
  ref: EntityRef;
  /** Display name for quick identification on the status board */
  name: string;
  /** Optional health tracking, values provided by callers */
  health?: StatusHealth | undefined;
  /** Optional armor override (display-only) */
  armor?: number | undefined;
  /** Optional will/spell defense override (display-only) */
  will?: number | undefined;
  /** Free-form tags such as "Carrying Treasure", "Encumbered" */
  tags?: string[] | undefined;
  /** Free-form notes for table state */
  notes?: string | undefined;
}

export interface PlayState {
  phase?: Phase | undefined;
  statuses: Record<string, Status>;
}

interface PlayStore extends PlayState {
  // Actions
  setPhase: (phase?: Phase) => void;
  upsertStatus: (entityId: string, status: Status) => void;
  patchStatus: (entityId: string, patch: Partial<Status>) => void;
  removeStatus: (entityId: string) => void;
  clearAll: () => void;
}

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------

const INITIAL_STATE: PlayState = {
  phase: undefined,
  statuses: {},
};

const STORAGE_KEY = 'fwbt.play.v1';

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const usePlayStore = create<PlayStore>()(
  persist(
    immer((set) => ({
      ...INITIAL_STATE,

      setPhase: (phase) => {
        set((state) => {
          state.phase = phase;
        });
      },

      upsertStatus: (entityId, status) => {
        set((state) => {
          state.statuses[entityId] = status;
        });
      },

      patchStatus: (entityId, patch) => {
        set((state) => {
          const existing = state.statuses[entityId];
          if (!existing) return;

          state.statuses[entityId] = {
            ...existing,
            ...patch,
            health:
              patch.health !== undefined || existing.health !== undefined
                ? {
                    ...(existing.health ?? {}),
                    ...(patch.health ?? {}),
                  }
                : undefined,
            tags:
              patch.tags !== undefined
                ? [...patch.tags]
                : existing.tags !== undefined
                  ? [...existing.tags]
                  : undefined,
          };
        });
      },

      removeStatus: (entityId) => {
        set((state) => {
          delete state.statuses[entityId];
        });
      },

      clearAll: () => {
        set(() => ({
          ...INITIAL_STATE,
        }));
      },
    })),
    {
      name: STORAGE_KEY,
      version: 1,
      partialize: (state) => ({
        phase: state.phase,
        statuses: state.statuses,
      }),
    }
  )
);

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectPhase = (state: PlayStore) => state.phase;

export const selectStatuses = (state: PlayStore) => state.statuses;

export const selectStatus =
  (entityId: string) =>
  (state: PlayStore): Status | undefined =>
    state.statuses[entityId];


